import * as Sentry from '@sentry/react';

import {store} from '@/app/store';
import {setWebView, unSetWebView} from '@/app/store/deviceSlice';
import {setTokenData} from '@/app/store/tokenSlice';
import {decodeToken} from '@/app/utils/AuthenticationUtils';
import {obscureName} from '@/utils/obscureName';

import {WEB_VIEW_PROPERTIES} from './constants';

declare global {
  interface Window {
    ReactNativeWebView: any;
  }
}

export const WebViewService = (() => {
  let webviewToken = localStorage.getItem(WEB_VIEW_PROPERTIES.AUTHENTICATION);
  let webviewRefreshToken = localStorage.getItem(
    WEB_VIEW_PROPERTIES.REFRESH_TOKEN,
  );
  const webviewReturnUrl = localStorage.getItem(WEB_VIEW_PROPERTIES.RETURN_URL);

  let isWebView = !!window.ReactNativeWebView;

  const initWebView = (): void => {
    webviewToken = localStorage.getItem(WEB_VIEW_PROPERTIES.AUTHENTICATION);
    webviewRefreshToken = localStorage.getItem(
      WEB_VIEW_PROPERTIES.REFRESH_TOKEN,
    );

    // The mobile app injects these into localStorage only to hand them off on
    // load. Once consumed into memory/Redux, wipe them so they aren't left
    // sitting in localStorage indefinitely, readable by any script in the
    // origin (e.g. via XSS).
    localStorage.removeItem(WEB_VIEW_PROPERTIES.AUTHENTICATION);
    localStorage.removeItem(WEB_VIEW_PROPERTIES.REFRESH_TOKEN);

    if (webviewToken) {
      setAuthentication();
      handleSetWebView();
    } else {
      handleUnSetWebView();
    }
  };

  const transformWebViewTokenInUserWeb = () => {
    if (!webviewToken) return null;

    const tokenDecoded = decodeToken(webviewToken);

    return {
      id: tokenDecoded?.user_uuid,
      name: tokenDecoded?.name,
      email: tokenDecoded?.email,
      phone: tokenDecoded?.phone_number,
      role: tokenDecoded?.role,
      accessToken: webviewToken,
      refreshToken: webviewRefreshToken,
    };
  };

  const setAuthentication = (): void => {
    const authentication = transformWebViewTokenInUserWeb();
    if (authentication) {
      store.dispatch(setTokenData(authentication));
      const userData: Sentry.User = {
        id: authentication.id,
        name: obscureName(authentication.name),
        role: authentication.role,
      };
      Sentry.setUser(userData);
    }
  };

  const handleSetWebView = (): void => {
    isWebView = true;
    store.dispatch(setWebView());
  };

  const handleUnSetWebView = (): void => {
    isWebView = false;
    store.dispatch(unSetWebView());
  };

  const getReturnUrl = (): string | null => {
    return webviewReturnUrl;
  };

  const isWebViewActive = (): boolean => {
    return isWebView;
  };

  const getUserRole = (): string | null => {
    if (!webviewToken) return null;

    const tokenDecoded = decodeToken(webviewToken);
    return tokenDecoded?.role || null;
  };

  return {
    initWebView,
    getReturnUrl,
    isWebViewActive,
    handleUnSetWebView,
    getUserRole,
  };
})();
